/**
 * Stable-enough browser fingerprint for device-slot reuse after localStorage wipe.
 * Not a security boundary — pairs with server-side device_id matching.
 */

let cachedFingerprint: string | null = null

function canvasSignal(): string {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 240
    canvas.height = 60
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    ctx.textBaseline = 'top'
    ctx.font = '16px "Arial"'
    ctx.fillStyle = '#f60'
    ctx.fillRect(10, 10, 120, 40)
    ctx.fillStyle = '#069'
    ctx.fillText('m2mba-fp', 14, 18)
    ctx.strokeStyle = '#ff0'
    ctx.beginPath()
    ctx.arc(180, 30, 18, 0, Math.PI * 2)
    ctx.stroke()
    return canvas.toDataURL().slice(-96)
  } catch {
    return ''
  }
}

async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const data = new TextEncoder().encode(input)
    const digest = await crypto.subtle.digest('SHA-256', data)
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }

  // Fallback (non-crypto) — still long enough for server MIN_FINGERPRINT_LEN
  let h = 0
  for (let i = 0; i < input.length; i += 1) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0
  }
  const seed = `${h.toString(16)}${input.length}`
  return `${seed}${seed}${seed}${seed}`.slice(0, 64)
}

function collectSignals(): string {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator)
  const scr = typeof screen !== 'undefined' ? screen : ({} as Screen)
  const parts = [
    nav.userAgent || '',
    nav.language || '',
    (nav.languages || []).join(','),
    nav.platform || '',
    String(nav.hardwareConcurrency || ''),
    String((nav as Navigator & { deviceMemory?: number }).deviceMemory || ''),
    String(nav.maxTouchPoints || 0),
    String(scr.width || ''),
    String(scr.height || ''),
    String(scr.colorDepth || ''),
    String(scr.pixelDepth || ''),
    String(typeof window !== 'undefined' ? window.devicePixelRatio || '' : ''),
    String(new Date().getTimezoneOffset()),
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    canvasSignal(),
  ]
  return parts.join('|')
}

/** SHA-256 hex of browser signals; cached for the page lifetime. */
export async function getDeviceFingerprint(): Promise<string> {
  if (cachedFingerprint && cachedFingerprint.length >= 32) {
    return cachedFingerprint
  }
  const hash = await sha256Hex(collectSignals())
  cachedFingerprint = hash
  return hash
}
